import { Link, useLocation } from "wouter";
import { useContainers } from "../../api/queries";
import type { Container, PortBinding } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { ContainerStatePill } from "../../components/StatePill";
import { PageHeader } from "../../components/PageHeader";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { useFleet } from "../../fleet/FleetContext";

function formatPorts(ports: PortBinding[]): string {
  if (ports.length === 0) return "—";
  return ports
    .map((p) => (p.hostPort ? `${p.hostPort}→${p.containerPort}/${p.protocol}` : `${p.containerPort}/${p.protocol}`))
    .join(", ");
}

const COLUMNS: DataTableColumn<Container>[] = [
  {
    key: "name",
    header: "Name",
    cell: (container) => (
      <>
        {/* stopPropagation: the row itself is also a click target (DataTable's
            onRowClick); without this, activating the link would navigate twice. */}
        <Link
          className="data-table__primary-link"
          href={`/containers/${container.nodeId}/${container.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          {container.name}
        </Link>
        {container.stackName && <div className="data-table__secondary">{container.stackName}</div>}
      </>
    ),
  },
  { key: "image", header: "Image", cell: (c) => c.image, className: "data-table__mono" },
  { key: "state", header: "State", cell: (c) => <ContainerStatePill state={c.state} /> },
  { key: "node", header: "Node", cell: (c) => c.nodeName },
  { key: "ports", header: "Ports", cell: (c) => formatPorts(c.ports), className: "data-table__mono" },
  { key: "status", header: "Status", cell: (c) => c.status, className: "data-table__secondary" },
];

export function ContainersListPage() {
  const { currentId, isLoading: fleetLoading } = useFleet();
  const [, navigate] = useLocation();
  const paginator = usePaginator(currentId);
  const { data, isPending, isError, error, refetch, isFetching } = useContainers(currentId, {
    cursor: paginator.cursor,
  });

  return (
    <>
      <PageHeader
        title="Containers"
        description="Every container Comebye can see across the current fleet's nodes."
      />

      <QueryStates
        isLoading={fleetLoading || isPending}
        loadingLabel="Loading containers…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={error instanceof Error ? error.message : "Couldn't load containers. Check your connection and try again."}
        isEmpty={Boolean(data && data.items.length === 0)}
        empty={
          <EmptyState
            title="No containers here yet"
            description="This fleet has no containers reporting in. Once a node enrolls and runs workloads, they'll show up here."
          />
        }
      >
        {data && (
          <>
            <DataTable
              columns={COLUMNS}
              rows={data.items}
              rowKey={(container) => container.id}
              onRowClick={(container) => navigate(`/containers/${container.nodeId}/${container.id}`)}
              ariaBusy={isFetching}
            />
            <PaginatorControls
              itemCount={data.items.length}
              nextCursor={data.nextCursor}
              paginator={paginator}
              noun="containers"
            />
          </>
        )}
      </QueryStates>
    </>
  );
}
